import { indexViewBlocks, pageDocument } from '../doc-blocks'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { CoreState } from './state'
import type { EditorIntent } from './intents'
import { children, plain, pointAt, rows } from './tree-read'
import { fieldBlocks } from './scope'
import { setBlockProps } from './ops'
import { transaction } from './transaction'

export function nestedQuestions(blocks: Blocks): Extract<Block, { type: 'question' }>[] {
  return blocks.flatMap(block => block.type === 'question' ? [block] : nestedQuestions(children(block)))
}

/** Same option permutation and correct-answer remapping as question-node $moveQuestionOption/$removeQuestionOptionField. */
export function questionAction(state: CoreState, intent: Extract<EditorIntent, { type: 'questionAction' }>) {
  const block = structuredClone(indexViewBlocks(pageDocument(state.doc)).get(intent.block)?.block) as Extract<Block, { type: 'question' }> | undefined
  if (!block) return
  const { action, index } = intent
  if ((block.subtype === 'sc' || block.subtype === 'mc') && action.includes('option')) {
    const order = block.opts.map((_, i) => i)
    if (action === 'remove-option' && order.length > 1) order.splice(index, 1)
    // One gesture, one operation: a drag names its destination (`to`), the menu moves one step.
    const target = action === 'move-option' ? intent.to : action === 'move-option-up' ? index - 1 : action === 'move-option-down' ? index + 1 : undefined
    if (target !== undefined) {
      if (target < 0 || target >= order.length || target === index) return
      order.splice(index, 1)
      order.splice(target, 0, index)
    }
    // Native list edits keep each option's slot entity (INV-SLOT), so a caret's field follows its option.
    const tx = transaction(state)
    const path = indexViewBlocks(tx.read('')).get(block.id)?.path
    if (!path) throw new Error(`Question ${block.id} is not attached`)
    if (action === 'remove-option' && order.length < block.opts.length) tx.remove(`${path}.opts`, index, 1)
    if (target !== undefined) tx.move(`${path}.opts`, index, target)
    // add-option inserts an empty option at `index` in the same operation (Enter below an option, Add at the end).
    if (action === 'add-option') { tx.insertLists(`${path}.opts`, index, [[]]); order.splice(index, 0, -1) }
    const next = (i: number) => order.indexOf(i)
    // A removed correct option passes correctness to the option now at its place (Lexical $removeQuestionOptionField).
    const answer = block.subtype === 'sc' ? next(block.answer) >= 0 ? next(block.answer) : Math.min(index, order.length - 1)
      : block.answer.map(i => next(i)).filter(i => i >= 0)
    if (JSON.stringify(answer) !== JSON.stringify(block.answer)) tx.set(path, 'answer', answer)
    // Typing continues in the new option, or at the end of the option before a removed one (the next one when the first
    // is removed), as Lexical $insertQuestionOptionAfter / $removeQuestionOptionField select it. The command owns this
    // caret, so history restores it and every host shows it.
    const focus = action === 'add-option' ? index : action === 'remove-option' && order.length < block.opts.length ? Math.max(0, index - 1) : undefined
    if (focus !== undefined) {
      state.scope = { blockId: block.id, field: 'opts', index: focus, slot: tx.entities(`${path}.opts`)[focus] }
      state.blocks = fieldBlocks(order[focus] < 0 ? [] : block.opts[order[focus]])
      const last = rows(state.blocks).at(-1)
      if (!last) throw new Error(`INV-FIELD: option ${focus} of ${block.id} has no row`)
      const caret = pointAt(last, plain(last.block).length)
      state.selection = { anchor: caret, focus: caret }
      state.pendingFormat = undefined
    }
  } else if (block.subtype === 'la' && action === 'remove-criterion') {
    setBlockProps(state, block, { criteria: block.criteria.filter((_, i) => i !== index) })
  } else if (block.subtype === 'sa') {
    if (action === 'remove-answer') setBlockProps(state, block, { answer: block.answer.filter((_, i) => i !== index) })
    if (action === 'add-answer') setBlockProps(state, block, { answer: [...block.answer, ''] })
  }
}
